import { FC, useCallback, useState } from 'react';
import { css } from '@emotion/react';
import { Feature } from '@weave/schema-gen-ts/dist/shared/feature/location_feature.pb';
import { SubscriptionsQueries } from '@frontend/api-subscription';
import { getUser } from '@frontend/auth-helpers';
import { useTranslation } from '@frontend/i18n';
import { sentry } from '@frontend/tracking';
import { theme } from '@frontend/theme';
import {
  ContentLoader,
  Modal,
  ModalControlModalProps,
  Text,
  TextareaField,
  useAlert,
  useControlledField,
} from '@frontend/design-system';
import { getFeatureWiseTrackingId, RequestUpgradeModalTrackingIds } from '../../constants/tracking-ids';

interface RequestUpgradeModalProps extends ModalControlModalProps {
  feature: Feature;
  title?: string;
  description?: string;
  selectedLocationIds?: string[];
  onActionSuccess?: () => void;
}

const NOTE_MAX_LENGTH = 500;

export const RequestUpgradeModal: FC<RequestUpgradeModalProps> = ({
  onActionSuccess,
  feature,
  selectedLocationIds,
  title,
  description,
  ...modalProps
}) => {
  const { t } = useTranslation('featurePromotion', { keyPrefix: 'request-upgrade' });
  const alerts = useAlert();

  const [notes, setNotes] = useState('');

  const { mutate: notifyAdmin, isLoading: isMutating } = SubscriptionsQueries.useNotifyAdminForUpgrade({
    mutationKey: [selectedLocationIds?.[0], feature, 'notify-admin'],
    onSuccess: () => {
      alerts.success(t('Your admin will be notified of your request by email'));
      onActionSuccess?.();
    },
    onError: (error) => {
      alerts.error(t('Failed to notify admin'));
      sentry.error({
        team: 'fe-clm',
        topic: 'customer-lifecycle-management',
        error: `Failed to notify admin for upgrade: ${error}`,
      });
    },
  });

  const noteField = useControlledField({
    onChange: (text) => setNotes(text.slice(0, NOTE_MAX_LENGTH)),
    type: 'text',
    value: notes,
  });

  const handleAction = useCallback(() => {
    notifyAdmin({
      locationId: selectedLocationIds?.[0] || '',
      featureEnum: feature,
      notes,
      firstName: getUser()?.firstName,
    });
  }, [notifyAdmin, selectedLocationIds, feature, notes]);

  return (
    <Modal {...modalProps} minWidth={600}>
      <ContentLoader show={isMutating} />
      <Modal.Header
        onClose={modalProps.onClose}
        closeBtnTrackingId={getFeatureWiseTrackingId(feature, RequestUpgradeModalTrackingIds.dismissAction)}
      >
        {title || t('Upgrade Request')}
      </Modal.Header>
      <Modal.Body css={modalBodyStyles}>
        <Text size='medium'>
          {description || t('Add a note to your admin about why you want access to this feature.')}
        </Text>
        <TextareaField
          {...noteField}
          label={t('Note')}
          name={'note'}
          helperText={`${notes.length}/${NOTE_MAX_LENGTH}`}
        />
      </Modal.Body>
      <Modal.Footer
        primary={{
          label: t('Request Upgrade'),
          onClick: handleAction,
          disabled: !notes.length || isMutating,
          trackingId: getFeatureWiseTrackingId(feature, RequestUpgradeModalTrackingIds.requestUpgradeAction),
        }}
      />
    </Modal>
  );
};

const modalBodyStyles = css({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  position: 'relative',
});
